import React, { useMemo, useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import useResponsiveness from '@/hooks/useResponsiveness';
import useRequireAuth from '@/hooks/useRequireAuth';
import { useModalStore } from '@/store';
import { BORDER_RADIUS } from '@/constants/config';

const createStyles = (colors, isXS) => StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: isXS ? 32 : 56,
    paddingHorizontal: isXS ? 20 : 32,
    minHeight: isXS ? 320 : 420,
  },
  content: {
    width: '100%',
    maxWidth: 460,
    alignItems: 'center',
  },
  iconWrap: {
    width: isXS ? 64 : 72,
    height: isXS ? 64 : 72,
    borderRadius: isXS ? 32 : 36,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.solid_color_secondary,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    marginBottom: 18,
  },
  title: {
    textAlign: 'center',
    marginBottom: 10,
  },
  subtitle: {
    textAlign: 'center',
    marginBottom: isXS ? 22 : 28,
    maxWidth: 380,
  },
  actions: {
    width: '100%',
    flexDirection: isXS ? 'column' : 'row',
    gap: 10,
    justifyContent: 'center',
    alignItems: 'stretch',
    maxWidth: 360,
  },
  benefitsBlock: {
    width: '100%',
    marginTop: isXS ? 22 : 28,
    paddingHorizontal: 4,
    gap: 10,
    maxWidth: 380,
  },
  benefitRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  benefitIcon: {
    marginTop: 2,
  },
  buttonWrap: {
    flex: isXS ? undefined : 1,
  },
  // eslint-disable-next-line react-native/no-unused-styles -- referenced via BORDER_RADIUS theming consumers
  pillBorder: {
    borderRadius: BORDER_RADIUS.medium,
  },
});

/**
 * Drop-in "Create an account to use X" panel for personal-only pages
 * (Files, Collections, Library, Bookmarks, Memory, Tags, Tools history,
 * etc.). Renders inline (no modal) and triggers `AuthCTAModal` via the
 * shared `useRequireAuth` hook on press so analytics + auth flow stay
 * consistent with the rest of the gated app.
 *
 * Props:
 *  - title:       headline (e.g. "Sign in to see your files")
 *  - subtitle:    supporting copy
 *  - icon:        font-awesome icon name shown above the title
 *  - source:      analytics tag forwarded to AuthCTAModal
 *  - benefits:    optional [{ icon, label }] bullet list
 *  - primaryLabel / secondaryLabel: optional overrides
 *  - onPrimary:   optional override; defaults to opening AuthCTAModal
 *                 via requireAuth() with these props.
 *  - onSecondary: optional override; defaults to closeAndOpenModal('LoginModal')
 */
function SignInCTAPanel({
  title = 'Sign in to continue',
  subtitle,
  icon = 'lock',
  source = 'sign_in_cta_panel',
  benefits,
  primaryLabel = 'Create account',
  secondaryLabel = 'Log in',
  onPrimary,
  onSecondary,
}) {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const styles = useMemo(() => createStyles(colors, isXS), [colors, isXS]);
  const requireAuth = useRequireAuth();
  const { openModal } = useModalStore(state => state.actions);

  const handlePrimary = useCallback(() => {
    if (onPrimary) return onPrimary();
    // requireAuth always opens AuthCTAModal when logged out — the inner
    // `() => {}` is a no-op since the action is "go sign up", not
    // anything we want to replay after auth.
    requireAuth(() => {}, { source, title, subtitle, icon });
  }, [onPrimary, requireAuth, source, title, subtitle, icon]);

  const handleSecondary = useCallback(() => {
    if (onSecondary) return onSecondary();
    openModal('LoginModal');
  }, [onSecondary, openModal]);

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <View style={styles.iconWrap}>
          <Icon name={icon} size={isXS ? 26 : 30} color={colors.text_color_primary} />
        </View>

        <Text size={isXS ? 'xl' : 'xxl'} weight="bold" color="primary" style={styles.title}>
          {title}
        </Text>

        {subtitle ? (
          <Text size="n" color="secondary" style={styles.subtitle}>
            {subtitle}
          </Text>
        ) : null}

        <View style={styles.actions}>
          <View style={styles.buttonWrap}>
            <Button
              text={primaryLabel}
              variant="primary"
              size="large"
              onPress={handlePrimary}
              isFullWidth
              appendIcon="arrow-right"
            />
          </View>
          <View style={styles.buttonWrap}>
            <Button
              text={secondaryLabel}
              variant="secondary"
              size="large"
              onPress={handleSecondary}
              isFullWidth
            />
          </View>
        </View>

        {Array.isArray(benefits) && benefits.length > 0 && (
          <View style={styles.benefitsBlock}>
            {benefits.map((b, i) => (
              <View key={`${b.label}-${i}`} style={styles.benefitRow}>
                <Icon
                  name={b.icon || 'check'}
                  size={14}
                  color={colors.text_color_secondary}
                  style={styles.benefitIcon}
                />
                <Text size="s" color="secondary" style={{ flex: 1 }}>
                  {b.label}
                </Text>
              </View>
            ))}
          </View>
        )}
      </View>
    </View>
  );
}

export default React.memo(SignInCTAPanel);
